import { UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { QueueItem, QueueItemSchema, QueueItemStatus, QueueItemStatusSchema } from 'api/queueItems/queueItems.types'
import { queryClient } from 'helpers/queryClient'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { ALERT_KEYS } from 'store/alert/alert.constants'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { SubtypeObject } from 'ui/components/NotificationCenter/NotificationCenter.helper'
import Datetime from 'utils/datetime/datetime'
import { RecursiveMap } from 'views/OptimizationAnalysisView/types'
import z from 'zod'

export type OptJobType =
  | `regular`
  | `regular_wip`
  | `followup_with_deviations`
  | `followup_without_deviations`
  | `followup_regular`
  | `measvalues_calculations`
  | `marginal_cost_calculation`
  | `sandbox`
  | 'real_time'
  | 'real_time_measvalues_calculations'
  | 'fuel_plan'
  | 'electricity_price_scenario'
  | 'intraday_plan'
  | 'settlement'

// The status 'Any' is used when getOptJobLatestKey for useQuery and invalidateQueries when the status is not defined
const OptJobStatusSchema = z.enum([`Created`, `Queued`, `Running`, `Failed`, `Finished`, `Unsolvable`, `Any`])
export type OptJobStatus = z.infer<typeof OptJobStatusSchema>

// Terminal opt-job statuses: the job has reached an end state and will not progress further.
// Kept here as the single source of truth so views (e.g. IntradayTradingView) and progress
// components stay aligned.
export const TERMINAL_OPT_JOB_STATUSES: ReadonlySet<OptJobStatus> = new Set(['Finished', 'Failed', 'Unsolvable'])
export const FAILED_OPT_JOB_STATUSES: ReadonlySet<OptJobStatus> = new Set(['Failed', 'Unsolvable'])
export const ACTIVE_OPT_JOB_STATUSES: ReadonlySet<OptJobStatus> = new Set(['Created', 'Queued', 'Running'])

// Terminal queue-item statuses: the queue item has reached an end state and no more phase
// timestamps will be emitted.
const TERMINAL_QUEUE_ITEM_STATUSES: readonly QueueItemStatus[] = ['Finished', 'Failed', 'Unsolvable'] as const

export function isTerminalQueueItemStatus(status: QueueItemStatus): boolean {
  return (TERMINAL_QUEUE_ITEM_STATUSES as readonly QueueItemStatus[]).includes(status)
}

// The queue item ended without a usable result — the solve either broke down (`Failed`) or ran
// to completion and found no feasible solution (`Unsolvable`). Both leave the user with nothing
// to act on, so the two are one case everywhere except where the wording explains which it was.
const FAILED_QUEUE_ITEM_STATUSES: readonly QueueItemStatus[] = ['Failed', 'Unsolvable'] as const

export function isFailedQueueItemStatus(status: QueueItemStatus | null): boolean {
  return status !== null && FAILED_QUEUE_ITEM_STATUSES.includes(status)
}

export type OptJobUnitSetting = {
  start_level?: number | null
  end_level?: number | null
}

export type OptJobInputData = {
  opt_start_time: string
  opt_end_time?: string
  unit_settings?: Record<string, OptJobUnitSetting>
  include_subtypes?: string[]
  intraday_prices?: { times: string[]; values: number[] }
  input_data_overrides?: Record<string, unknown>
}

type OptJobPostData = {
  opt_model?: number
  opt_job_type: OptJobType
  input_data?: OptJobInputData
}

export type OptJobStatusObject = {
  created_at: string
  id: number
  opt_job_type: OptJobType
  opt_model?: number
  digital_twin?: number
  system?: number
  status: OptJobStatus
  updated_at: string
  subtypes?: SubtypeObject[]
}

export type LatestQueueItem = {
  subtype: string
  opt_job_type: OptJobType
  opt_job_id: number
  queue_item_id: number
  created_at: string
  updated_at: string
  opt_job_status: OptJobStatus
  queue_item_status?: QueueItemStatus
  created_by: string
}

export type LatestQueueItemResponse = {
  data: LatestQueueItem[]
}

type QueueItemResultItem = {
  time: string
  costs: RecursiveMap<string, number>
  production: RecursiveMap<string, number>
}

type QueueItemResultSolution = {
  data: QueueItemResultItem[]
  [key: string]: undefined | QueueItemResultItem[]
}

type QueueItemResult = {
  solution: QueueItemResultSolution
  [key: string]: undefined | QueueItemResultSolution
}

type QueueItemResultTuple = [undefined, QueueItemResult]

export type QueueItemResultsResponse = QueueItemResultTuple[]

/**
 * Skipped subtype info from the publish API response.
 * Matches backend's PublishSkippedSubtypeResponse TypedDict.
 */
export type PublishSkippedSubtype = {
  subtype: string | null
  reason: string
  latest_regular_opt_job_id: number
  latest_regular_created_at: string // ISO format string
}

/**
 * Response from publishing a regular_wip optimization job.
 * Returns the newly created regular optimization job that was published from the WIP.
 */
export type PublishRegularWipResponse = {
  id: number
  created_by: number
  opt_model: number | null
  digital_twin: number
  opt_job_type: OptJobType
  input_data: OptJobInputData
  status: OptJobStatus
  queue_items: unknown[]
  created_at: string
  updated_at: string
  url: string
  execution_settings: { [key: string]: unknown }
  published_subtypes: (string | null)[]
  skipped_subtypes: PublishSkippedSubtype[]
}

export const OPT_JOBS_QUERY_KEY = `optJobs`
export const OPT_JOBS_AND_QUEUE_ITEMS_STATUS_QUERY_KEY = `getOptJobAndQueueItemsStatus`
export const OPT_JOB_WITH_QUEUE_ITEMS_QUERY_KEY = `optJobWithQueueItems`

/**
 * Error codes returned by the publish_regular_wip endpoint.
 * These codes match the backend's PublishErrorCode enum.
 */
export const PUBLISH_REGULAR_WIP_ERROR_CODES = {
  MISSING_OPT_JOB_ID: 'missing_opt_job_id',
  INVALID_OPT_JOB_ID: 'invalid_opt_job_id',
  OPT_JOB_NOT_FOUND: 'opt_job_not_found',
  SYSTEM_NOT_FOUND: 'system_not_found',
  INVALID_OPT_JOB_TYPE: 'invalid_opt_job_type',
  INVALID_OPT_JOB_STATUS: 'invalid_opt_job_status',
  MISSING_DIGITAL_TWIN: 'missing_digital_twin',
  SUBTYPE_NOT_FOUND: 'subtype_not_found',
  ALL_SUBTYPES_SKIPPED: 'all_subtypes_skipped',
  OPT_JOB_ALREADY_RUNNING: 'opt_job_already_running',
  INVALID_PARAMETERS: 'invalid_parameters',
} as const

export type PublishRegularWipErrorCode =
  (typeof PUBLISH_REGULAR_WIP_ERROR_CODES)[keyof typeof PUBLISH_REGULAR_WIP_ERROR_CODES]

/**
 * Error response structure from the publish_regular_wip endpoint.
 */
export type PublishRegularWipErrorDetails = {
  // Common fields
  opt_job_id?: number | string
  // For invalid_opt_job_type
  expected_type?: string
  actual_type?: string
  // For invalid_opt_job_status
  expected_status?: string
  actual_status?: string
  // For subtype_not_found
  requested_subtype?: string
  // For all_subtypes_skipped
  skipped_subtypes?: Array<{
    subtype: string
    reason: string
    latest_regular_opt_job_id: number
    latest_regular_created_at: string
  }>
  // For opt_job_already_running
  running_opt_job_id?: number
  running_opt_job_status?: OptJobStatus
  running_opt_job_created_at?: string
  opt_job_type?: OptJobType
  conflicting_subtypes?: string[]
  [key: string]: unknown
}

export type PublishRegularWipErrorResponse = {
  error: {
    code: PublishRegularWipErrorCode
    message: string
    details?: PublishRegularWipErrorDetails
  }
  can_publish?: boolean
}

/**
 * Type guard to check if an error body is a PublishRegularWipErrorResponse.
 */
export function isPublishRegularWipErrorResponse(body: unknown): body is PublishRegularWipErrorResponse {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as PublishRegularWipErrorResponse).error === 'object' &&
    (body as PublishRegularWipErrorResponse).error !== null &&
    'code' in (body as PublishRegularWipErrorResponse).error &&
    typeof (body as PublishRegularWipErrorResponse).error.code === 'string'
  )
}

/**
 * Translation keys for publish_regular_wip error codes.
 * Maps backend error codes to user-friendly translated messages.
 */
export const PUBLISH_REGULAR_WIP_ERROR_TRANSLATION_KEYS: Record<PublishRegularWipErrorCode, string> = {
  [PUBLISH_REGULAR_WIP_ERROR_CODES.MISSING_OPT_JOB_ID]: 'publish_regular_wip_error.missing_opt_job_id',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.INVALID_OPT_JOB_ID]: 'publish_regular_wip_error.invalid_opt_job_id',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.OPT_JOB_NOT_FOUND]: 'publish_regular_wip_error.opt_job_not_found',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.SYSTEM_NOT_FOUND]: 'publish_regular_wip_error.system_not_found',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.INVALID_OPT_JOB_TYPE]: 'publish_regular_wip_error.invalid_opt_job_type',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.INVALID_OPT_JOB_STATUS]: 'publish_regular_wip_error.invalid_opt_job_status',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.MISSING_DIGITAL_TWIN]: 'publish_regular_wip_error.missing_digital_twin',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.SUBTYPE_NOT_FOUND]: 'publish_regular_wip_error.subtype_not_found',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.ALL_SUBTYPES_SKIPPED]: 'publish_regular_wip_error.all_subtypes_skipped',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.OPT_JOB_ALREADY_RUNNING]: 'publish_regular_wip_error.opt_job_already_running',
  [PUBLISH_REGULAR_WIP_ERROR_CODES.INVALID_PARAMETERS]: 'publish_regular_wip_error.invalid_parameters',
}

export function getOptJobLatestKey({
  type,
  status,
  sandboxProjectId,
  systemId,
  limit,
  subtypes = [],
}: {
  type: OptJobType
  sandboxProjectId?: number
  systemId?: number
  status?: OptJobStatus
  limit?: number
  subtypes?: string[]
}) {
  const params = []
  params.push(type)

  if (limit !== undefined) {
    params.push(limit)
  }

  if (status !== undefined) {
    params.push(status)
  } else {
    params.push('Any')
  }

  if (sandboxProjectId !== undefined) {
    params.push(sandboxProjectId)
  }

  if (systemId !== undefined) {
    params.push(systemId)
  }

  if (subtypes.length > 0) {
    params.push(subtypes.join(','))
  } else {
    params.push('NO_SUBTYPES')
  }

  return [OPT_JOBS_QUERY_KEY, 'latest', ...params]
}

export function getOptjobAsObject(
  latestOptJobs: OptJobStatusObject[] | undefined,
  index?: number
): OptJobStatusObject | undefined {
  const jobIndex = index ?? 0
  if (latestOptJobs) {
    if (latestOptJobs.length > jobIndex) {
      return latestOptJobs[jobIndex]
    }
  }

  return undefined
}

export function useLatestOptJobStatus({
  enabled,
  limit,
  sandboxProjectId,
  status,
  type,
  subtypes = [],
}: {
  enabled?: boolean
  limit?: number
  sandboxProjectId?: number
  status?: OptJobStatus
  type: OptJobType
  subtypes?: string[]
}): UseQueryResult<OptJobStatusObject[]> {
  const { activeSystem } = useAuth()

  const queryKey = getOptJobLatestKey({ sandboxProjectId, systemId: activeSystem?.id, type, status, limit, subtypes })
  return useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      const queryParams: {
        opt_job_type__name: OptJobType
        system?: number
        limit?: number
        sandbox_project?: number
        status?: OptJobStatus
        subtypes?: string[]
      } = {
        opt_job_type__name: type,
        limit: limit,
        status,
      }

      if (subtypes.length > 0) {
        queryParams.subtypes = subtypes
      }

      if (sandboxProjectId) {
        queryParams.sandbox_project = sandboxProjectId
      } else {
        queryParams.system = activeSystem?.id
      }

      const response = await apiClient<OptJobStatusObject[] | []>(`opt_job/latest_status`, {
        params: queryParams,
        addBracketsToArrayParamKeys: false,
      })
      return response ?? []
    },
    enabled: enabled ?? true,
  })
}

const FlattenedOptJobAndQueueItemSchema = z.object({
  opt_job_id: z.number(),
  opt_job_status: OptJobStatusSchema,
  opt_job_created_at: z.string(),
  opt_job_updated_at: z.string(),
  queue_item_id: z.number().nullable(),
  queue_item_status: QueueItemStatusSchema.nullable(),
  queue_item_subtype: z.string().nullable(),
})

export type FlattenedOptJobAndQueueItem = z.infer<typeof FlattenedOptJobAndQueueItemSchema>

export function useLatestOptJobAndQueueItemsStatus({
  limit,
  optJobType,
  digitalTwinUid,
  subtypes = [],
}: {
  limit?: number
  digitalTwinUid?: number
  optJobType: OptJobType
  subtypes?: string[]
}): UseQueryResult<FlattenedOptJobAndQueueItem[]> {
  const { activeSystem } = useAuth()
  const uid = digitalTwinUid ?? activeSystem?.primary_digital_twin?.uid

  return useQuery({
    queryKey: [OPT_JOBS_AND_QUEUE_ITEMS_STATUS_QUERY_KEY, optJobType, subtypes],

    queryFn: async ({ queryKey }) => {
      const response = await apiClient<FlattenedOptJobAndQueueItem[] | []>(`opt_job/queue_items_status`, {
        params: {
          opt_job_type__name: optJobType,
          limit: limit,
          digital_twin_uid: uid,
          subtypes: subtypes,
        },
        addBracketsToArrayParamKeys: false,
      })
      if (!response) {
        throw new Error(`No response received for query key: ${queryKey.join(', ')}`)
      }

      return response.map(FlattenedOptJobAndQueueItemSchema.parse)
    },
    enabled: !!uid,
  })
}

/**
 * Mutation hook to create an optimization job.
 *
 * Posts to `POST /api/opt_jobs/` which creates a new OptJob for the active system.
 *
 * @example
 * ```tsx
 * const { mutate: optJobCreate } = useOptJobMutation()
 *
 * // Create a regular optimization job
 * optJobCreate({ opt_job_type: 'regular', input_data: { opt_start_time: '...' } })
 *
 * // Create with specific subtypes
 * optJobCreate({ opt_job_type: 'regular', input_data: { opt_start_time: '...', include_subtypes: ['heat'] } })
 *
 * // Create without specifying subtypes (backend auto-restricts to allowed ones)
 * optJobCreate({ opt_job_type: 'regular', input_data: { opt_start_time: '...' } })
 * ```
 *
 * @remarks
 * **Request body:**
 * ```json
 * {
 *   "system": "integer",           // Active system ID (added automatically)
 *   "opt_job_type": "string",      // One of the OptJobType values (required)
 *   "opt_model": "integer",        // (optional) Opt model ID
 *   "input_data": {
 *     "opt_start_time": "string",  // ISO timestamp (required)
 *     "opt_end_time": "string",    // ISO timestamp (optional)
 *     "include_subtypes": ["string"], // (optional) Subtypes to run
 *     "unit_settings": {},         // (optional) Accumulator level overrides
 *     "intraday_prices": {}        // (optional) Price data for intraday plans
 *   }
 * }
 * ```
 *
 * **Optimistic updates:** Sets the latest opt job status to "Created" immediately via
 * `onMutate`, rolling back on error.
 *
 * **Backend manual-trigger validation (`enable_manually_trigger_opt_job`):**
 *
 * The backend enforces per-subtype manual-trigger restrictions via `SystemHasOptJobType`:
 * - If `include_subtypes` contains a disabled subtype → 400 error.
 * - If no `include_subtypes` specified and entries exist → backend auto-restricts to only
 *   subtypes with `enable_manually_trigger_opt_job=True`.
 * - If ALL subtypes are disabled → 400 error.
 * - If no `SystemHasOptJobType` entries exist for the system/job type → no restrictions
 *   (feature inactive).
 *
 * The frontend disables buttons when all subtypes are disabled (UX), but correctness
 * is enforced server-side.
 *
 * @returns React Query mutation result
 */
export function useOptJobMutation() {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: (data: OptJobPostData) =>
      apiClient(`opt_jobs`, {
        method: `POST`,
        data: { ...data, system: activeSystem?.id },
      }),

    onMutate: async ({ opt_job_type }: OptJobPostData) => {
      return setOptJobLatestQueryData({
        systemId: activeSystem?.id,
        type: opt_job_type,
        data: { status: `Created` },
      })
    },

    onError: (errors, { opt_job_type }, rollback) => {
      rollback?.()
      alertStore.error(t(`Something went wrong unfortunately, see error: `) + errors)
      queryClient.invalidateQueries({
        queryKey: getOptJobLatestKey({
          systemId: activeSystem?.id,
          type: opt_job_type,
        }),
        refetchType: 'inactive',
      })
    },

    onSuccess: (_, { opt_job_type, input_data }: OptJobPostData) => {
      queryClient.invalidateQueries({
        queryKey: getOptJobLatestKey({
          systemId: activeSystem?.id,
          type: opt_job_type,
          subtypes: input_data?.include_subtypes ?? [],
        }),
        refetchType: 'inactive',
      })
    },
  })
}

/**
 * Get a translated error message for a publish_regular_wip error.
 * Uses the error code to determine the appropriate translated message.
 * This ensures the client has full control over translations.
 */
export function getPublishRegularWipErrorMessage(
  error: unknown,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  // Check if error has body property (ApiError)
  const errorBody = (error as { body?: unknown })?.body

  if (isPublishRegularWipErrorResponse(errorBody)) {
    const { code } = errorBody.error
    const translationKey = PUBLISH_REGULAR_WIP_ERROR_TRANSLATION_KEYS[code]

    if (translationKey) {
      return t(translationKey)
    }

    // Unknown error code - use generic message
    return t('publish_regular_wip_error.unknown')
  }

  // Not a structured error response - use generic error message
  return t('publish_regular_wip_error.generic')
}

/**
 * Response from publishing the latest regular_wip optimization jobs.
 * Returns an array of newly created regular optimization jobs.
 * Multiple OptJobs may be created if the latest queue items for different subtypes
 * come from different source OptJobs.
 */
export type PublishLatestRegularWipResponse = PublishRegularWipResponse[]

/**
 * Mutation hook to publish the latest finished regular_wip optimization jobs for a system.
 *
 * This endpoint finds the most recent finished regular_wip queue items per subtype
 * and creates new regular OptJobs from them. Unlike usePublishRegularWipOptJobMutation,
 * the backend handles finding the latest queue items per subtype independently.
 *
 * @example
 * ```tsx
 * const { mutate: publishLatestRegularWip, isPending } = usePublishLatestRegularWipOptJobMutation()
 *
 * // Publish latest for current system
 * publishLatestRegularWip({})
 *
 * // Publish latest for specific system
 * publishLatestRegularWip({ systemId: 123 })
 *
 * // Publish latest for a specific subtype only
 * publishLatestRegularWip({ subtype: 'heat' })
 * ```
 *
 * @remarks
 * **Key behaviors:**
 * - **Lookback Period:** Only considers regular_wip queue items from the last 7 days
 * - **Per-Subtype Latest:** Finds the latest finished queue item per subtype independently
 * - **Multiple Results:** Returns an array because different subtypes might come from different source OptJobs
 * - **Async Processing:** Returned OptJobs have status "Created", processing happens in background
 * - **Incremental Publishing:** Only publishes subtypes where source regular_wip is newer than latest regular
 * - **Running Job Protection:** Fails if a regular OptJob is already running for the same subtypes
 *
 * **Backend manual-trigger validation (`enable_manually_trigger_opt_job`):**
 *
 * The backend enforces per-subtype manual-trigger restrictions via `SystemHasOptJobType`:
 * - If a specific `subtype` is requested and it has `enable_manually_trigger_opt_job=False` → 400 error.
 * - If no `subtype` specified and entries exist → backend auto-restricts to only subtypes
 *   with `enable_manually_trigger_opt_job=True` (disabled subtypes are excluded from publishing).
 * - If ALL subtypes are disabled → 400 error.
 * - If no `SystemHasOptJobType` entries exist for the system/job type → no restrictions
 *   (feature inactive, publishes all subtypes from source).
 *
 * The frontend disables the publish button when all subtypes are disabled (UX), but
 * correctness is enforced server-side.
 *
 * **Request body:**
 * ```json
 * {
 *   "system_id": "integer", // ID of the system to publish for (required)
 *   "subtype": "string" // (optional) Specific subtype to filter to
 * }
 * ```
 *
 * **Response:** Array of {@link PublishRegularWipResponse}
 *
 * **Error handling:** Uses {@link getPublishRegularWipErrorMessage} to translate error codes.
 * See {@link PUBLISH_REGULAR_WIP_ERROR_CODES} for possible error codes.
 *
 * @returns React Query mutation result with {@link PublishLatestRegularWipResponse} data
 */
export function usePublishLatestRegularWipOptJobMutation() {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: async ({
      systemId,
      subtype,
    }: {
      systemId?: number
      subtype?: string
    }): Promise<PublishLatestRegularWipResponse> => {
      const system_id = systemId ?? activeSystem?.id

      return apiClient<PublishLatestRegularWipResponse>(`opt_jobs/publish_latest_regular_wip`, {
        method: `POST`,
        data: { system_id, subtype },
      })
    },
    onError: (error) => {
      const errorMessage = getPublishRegularWipErrorMessage(error, t)
      alertStore.error(
        t('Failed to publish optimization result') + ': ' + errorMessage,
        undefined,
        ALERT_KEYS.productionPlanPublishFailed
      )
    },
    onSuccess: () => {
      alertStore.success(t('A production plan has been published.'), undefined, ALERT_KEYS.productionPlanPublished)
      queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, `latestQueueItem`, 'regular'] })
    },
  })
}

/**
 * Mutation hook to publish the latest finished regular_wip optimization job.
 *
 * This creates a new regular optimization job from the WIP (work-in-progress) version,
 * effectively "publishing" the WIP results to the main production plan.
 *
 * @example
 * ```tsx
 * const { mutate: publishRegularWip, isPending } = usePublishRegularWipOptJobMutation()
 *
 * // Publish all subtypes
 * publishRegularWip({})
 *
 * // Publish specific subtype
 * publishRegularWip({ subtype: 'heat' })
 * ```
 *
 * @remarks
 * **Request body:**
 * ```json
 * {
 *   "opt_job_id": "integer", // ID of the latest finished regular_wip opt job (determined automatically)
 *   "subtype": "string", // (optional) Specific subtype to publish. If omitted, all subtypes are published.
 * }
 * ```
 *
 * **Response:** {@link PublishRegularWipResponse}
 *
 * **Status transitions:**
 * - OptJob: `Created` → `Running` → `Finished` (or `Failed`)
 * - QueueItem: `Waiting` → `Running` → `Finished` (or `Failed`)
 *
 * **Error handling:** Uses {@link getPublishRegularWipErrorMessage} to translate error codes.
 * See {@link PUBLISH_REGULAR_WIP_ERROR_CODES} for possible error codes.
 *
 * @returns React Query mutation result with {@link PublishRegularWipResponse} data
 */
export function usePublishRegularWipOptJobMutation() {
  const { t } = useTranslation()

  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: async ({ subtype }: { subtype?: string }): Promise<PublishRegularWipResponse> => {
      // Get latest opt job id (with the optional subtype) to publish
      let latest = await apiClient<OptJobStatusObject[]>(`opt_job/latest_status`, {
        params: {
          opt_job_type__name: `regular_wip`,
          limit: 1,
          status: `Finished`,
          subtypes: subtype,
          system: activeSystem?.id,
        },
        addBracketsToArrayParamKeys: false,
      })

      // Filter only for searched subtype within optjob content
      if (subtype) {
        latest = latest.filter((l) => (l.subtypes || []).some((s) => s?.name === subtype))
      }
      const opt_job_id = latest?.[0]?.id

      const data = { opt_job_id, subtype }
      return apiClient<PublishRegularWipResponse>(`opt_jobs/publish_regular_wip`, {
        method: `POST`,
        data,
      })
    },
    onError: (error) => {
      const errorMessage = getPublishRegularWipErrorMessage(error, t)
      alertStore.error(
        t('Failed to publish optimization result') + ': ' + errorMessage,
        undefined,
        ALERT_KEYS.productionPlanPublishFailed
      )
    },
    onSuccess: () => {
      alertStore.success(t('A production plan has been published.'), undefined, ALERT_KEYS.productionPlanPublished)
      queryClient.invalidateQueries({ queryKey: [OPT_JOBS_QUERY_KEY, `latestQueueItem`, 'regular'] })
    },
  })
}

async function setOptJobLatestQueryData({
  systemId,
  type,
  status,
  data: newData,
}: {
  systemId?: number
  type: OptJobType
  status?: OptJobStatus
  data: Partial<OptJobStatusObject>
}) {
  const queryKey = getOptJobLatestKey({ systemId, type, status })
  await queryClient.cancelQueries({ queryKey })

  const previousOptJob = queryClient.getQueryData<OptJobStatusObject | null>(queryKey)

  queryClient.setQueryData<Partial<OptJobStatusObject> | null | undefined>(queryKey, (data) => {
    if (data == null) return { status: newData.status }
    return { ...data, ...newData }
  })

  return () => queryClient.setQueryData(queryKey, previousOptJob)
}

export function useRecentOptJobs({
  status,
  fromDate,
  toDate,
  limit = 25,
}: {
  status: OptJobStatus
  fromDate?: string
  toDate?: string
  limit?: number
}) {
  const { activeSystem } = useAuth()
  return useQuery({
    queryKey: [OPT_JOBS_QUERY_KEY, 'recent', activeSystem?.id, status, fromDate, toDate, limit],

    queryFn: async () => {
      const response = await apiClient<OptJobStatusObject[]>(`systems/${activeSystem?.id}/opt_jobs`, {
        params: {
          status,
          created_from: fromDate,
          created_to: toDate,
          limit,
        },
      })
      return response
    },
  })
}

export function useQueueItemResults({ optJobId }: { optJobId?: number }) {
  return useQuery({
    queryKey: [OPT_JOBS_QUERY_KEY, 'queueItems', 'results', optJobId],

    queryFn: async () => {
      const response = await apiClient<QueueItemResultsResponse>(`opt_jobs/${optJobId}/queue_item_results`)
      return response
    },

    enabled: !!optJobId,
  })
}

export function useLatestQueueItem({
  type,
  systemId,
  digitalTwinUid,
  optModelId,
  subtype,
  createdAfter,
  optJobStatus,
  queueItemStatus,
}: {
  // Note: One of systemId, digitalTwinUid and optModelId is required
  type: OptJobType
  systemId?: number
  digitalTwinUid?: number
  optModelId?: number
  subtype?: string
  createdAfter?: string
  optJobStatus?: OptJobStatus
  queueItemStatus?: QueueItemStatus
}): UseQueryResult<LatestQueueItem[]> {
  return useQuery({
    queryKey: [
      OPT_JOBS_QUERY_KEY,
      'latestQueueItem',
      type,
      systemId,
      digitalTwinUid,
      optModelId,
      subtype,
      createdAfter,
      optJobStatus,
      queueItemStatus,
    ],
    queryFn: async () => {
      // Default "createdAfter" to max 1 week if not defined
      if (!createdAfter) {
        const now = Datetime.getISONow(-24 * 7)
        createdAfter = Datetime.toISOString(now)
      }

      const response = await apiClient<LatestQueueItemResponse>(`opt_job/latest_queue_item`, {
        params: {
          opt_job_type__name: type,
          system_id: systemId,
          digital_twin_uid: digitalTwinUid,
          opt_model_id: optModelId,
          subtype,
          created_after: createdAfter,
          opt_job_status: optJobStatus,
          queue_item_status: queueItemStatus,
        },
      })
      return response.data
    },
  })
}

// The endpoint nests the queue item without its parent id, which is stamped on
// after parsing. Derived from the shared contract so a field added to
// `QueueItemSchema` cannot go missing here.
const OptJobWithQueueItemsQueueItemSchema = QueueItemSchema.omit({ opt_job: true })

/**
 * The single place the API's ISO 8601 UTC strings are narrowed to `ISODateTime`,
 * right where zod has just proven the value is a string. Everything downstream then
 * takes a typed timestamp instead of re-asserting one at each call site.
 */
const IsoDateTimeSchema = z.string().transform((value) => value as ISODateTime)

const OptJobWithQueueItemsResponseItemSchema = z.object({
  id: z.number(),
  opt_job_type: z.string(),
  status: OptJobStatusSchema,
  created_at: IsoDateTimeSchema,
  // Moves on any write, including writes that are not status transitions, so it
  // must never be used as a phase or life-cycle boundary.
  updated_at: IsoDateTimeSchema,
  // When `status` was last written. Terminal job statuses are sticky, so for a
  // terminal job this is the end of its whole life cycle, for failed and
  // successful jobs alike. Rows predating the field send null, and an API
  // deployment predating it omits the key.
  status_updated_at: IsoDateTimeSchema.nullish().transform((value) => value ?? null),
  // User id, null for system-triggered jobs.
  created_by: z
    .number()
    .nullish()
    .transform((value) => value ?? null),
  // The creator's full name. Null when there is no creator, and `""` when the
  // user has no first/last name set.
  creator_name: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  queue_items: z
    .array(OptJobWithQueueItemsQueueItemSchema)
    .nullable()
    .transform((value) => value ?? []),
  // Derived server-side from the prefetched queue_items, not the OptJob.subtypes
  // column (which is only populated on the digital-twin creation path and NULL
  // elsewhere) — the distinct, order-preserving subtypes across this job's queue items.
  subtypes: z
    .array(z.string().nullable())
    .nullish()
    .transform((value) => value ?? []),
})

type OptJobWithQueueItemsResponseItem = z.infer<typeof OptJobWithQueueItemsResponseItemSchema>

export type OptJobWithQueueItems = Omit<OptJobWithQueueItemsResponseItem, 'opt_job_type' | 'queue_items'> & {
  opt_job_type: OptJobType
  // `opt_job` is stamped onto each queue item from the parent `id` so this
  // matches the shared `QueueItem` contract consumed by the phase-progress hooks.
  queue_items: QueueItem[]
}

/**
 * Get OptJobs with their nested queue items for a given system.
 *
 * Powers the opt job log's per-phase progress view (`OptJobLogPhaseProgress`).
 *
 * @remarks
 * `GET /api/opt_job/with_queue_items/`.
 *
 * Query params (exactly one required):
 * - `systemId` → `system_id`: the system ID. Resolved to the active system when
 *   none of the three are passed.
 * - `digitalTwinUid` → `digital_twin_uid`: the digital twin UID.
 * - `optModelId` → `opt_model_id`: the opt model ID.
 *
 * Query params (optional):
 * - `optJobTypes` → `opt_job_type__name`: filter by opt job type name(s).
 * - `optJobIds` → `opt_job_id`: filter to specific opt job ID(s).
 * - `optJobStatuses` → `opt_job_status`: filter by OptJob status (e.g.
 *   `"Finished"`, `"Running"`).
 * - `createdAfter` → `created_after`: limit to jobs created at or after this
 *   datetime (ISO datetime).
 * - `createdBefore` → `created_before`: limit to jobs created at or before this
 *   datetime (ISO datetime).
 * - `manuallyTriggeredOnly` → `manually_triggered_only`: limit to manually
 *   triggered jobs (default: false).
 * - `limit`: maximum number of jobs to return (default: 50, max: 500).
 * - `offset`: number of jobs to skip for pagination.
 *
 * Returns a list of OptJobs, each with:
 * - `id`: the OptJob ID.
 * - `opt_job_type`: the opt job type name.
 * - `status`: the OptJob status.
 * - `created_at` / `updated_at`: creation / last-update timestamps.
 * - `status_updated_at`: timestamp of the last status change.
 * - `created_by` / `creator_name`: the ID and display name of the user who
 *   created the opt job (or null).
 * - `queue_items`: the OptJob's nested queue items, each stamped with `opt_job`
 *   (its parent's `id`) to match the shared `QueueItem` contract.
 * - `subtypes`: the distinct subtypes across the OptJob's queue items.
 *
 * Defaults that catch callers out:
 * - Rows are ordered `created_at` descending, then `id` descending.
 * - `limit` defaults to **50** server-side and is capped at 500.
 * - Without `createdAfter` the window starts at midnight 7 days ago, and any
 *   `createdAfter` older than 30 days is silently clamped, not rejected.
 * - `optJobIds` escapes the date window entirely — the supported way to refetch
 *   one job that has aged past the lookback.
 *
 * Pagination is manual (`limit`/`offset`); the backend does not return a total count.
 */

/* Only show these job types in the log, because the others are either internal-only or not relevant to the user. */
export const OPT_JOB_TYPES_VISIBLE_IN_LOG: OptJobType[] = [
  'regular',
  'regular_wip',
  'followup_with_deviations',
  'followup_without_deviations',
  'followup_regular',
  'real_time',
  'marginal_cost_calculation',
  'fuel_plan',
  'electricity_price_scenario',
  'intraday_plan',
  'settlement',
]
export function useOptJobsWithQueueItems({
  systemId,
  digitalTwinUid,
  optModelId,
  optJobTypes,
  optJobIds,
  optJobStatuses,
  createdAfter,
  createdBefore,
  manuallyTriggeredOnly,
  limit,
  offset,
  enabled,
}: {
  systemId?: number
  digitalTwinUid?: number
  optModelId?: number
  optJobTypes?: OptJobType[]
  optJobIds?: number[]
  optJobStatuses?: OptJobStatus[]
  createdAfter?: string
  createdBefore?: string
  manuallyTriggeredOnly?: boolean
  limit?: number
  offset?: number
  enabled?: boolean
}): UseQueryResult<OptJobWithQueueItems[]> {
  const { activeSystem } = useAuth()
  const resolvedSystemId = systemId ?? (digitalTwinUid || optModelId ? undefined : activeSystem?.id)

  return useQuery({
    queryKey: [
      OPT_JOB_WITH_QUEUE_ITEMS_QUERY_KEY,
      {
        systemId: resolvedSystemId,
        digitalTwinUid,
        optModelId,
        optJobTypes,
        optJobIds,
        optJobStatuses,
        createdAfter,
        createdBefore,
        manuallyTriggeredOnly,
        limit,
        offset,
      },
    ],
    queryFn: async () => {
      const response = await apiClient<unknown[]>(`opt_job/with_queue_items`, {
        params: {
          system_id: resolvedSystemId,
          digital_twin_uid: digitalTwinUid,
          opt_model_id: optModelId,
          opt_job_type__name: optJobTypes,
          opt_job_id: optJobIds,
          opt_job_status: optJobStatuses,
          created_after: createdAfter,
          created_before: createdBefore,
          manually_triggered_only: manuallyTriggeredOnly,
          limit,
          offset,
        },
        addBracketsToArrayParamKeys: false,
      })
      return response.map((job) => {
        const parsed = OptJobWithQueueItemsResponseItemSchema.parse(job)
        return {
          ...parsed,
          opt_job_type: parsed.opt_job_type as OptJobType,
          queue_items: parsed.queue_items.map((item) => ({ ...item, opt_job: parsed.id })),
        }
      })
    },
    // The log panel unmounts when closed, and while it is closed no Pusher event
    // touches this cache — so what is cached can be arbitrarily far behind by the
    // time the user opens the panel again. Reopening must show the real state of
    // today's optimizations, which the global 10-minute `staleTime` would not.
    refetchOnMount: 'always',
    enabled: enabled ?? !!(resolvedSystemId ?? digitalTwinUid ?? optModelId),
  })
}

/**
 * `subtypes` the way the endpoint derives it: the distinct subtypes across the job's
 * queue items, in the order those items first name them. Kept in sync here because an
 * OptJob is created before its queue items exist — a job fetched in that window carries
 * an empty list, and patching an item in without re-deriving would leave the job
 * claiming it covers no subtypes for as long as it stays cached.
 */
function deriveSubtypesFromQueueItems(queueItems: QueueItem[]): (string | null)[] {
  const seen = new Set<string | null>()

  return queueItems.reduce<(string | null)[]>((subtypes, item) => {
    // An item without a subtype is one the server reads back as NULL, not as absent.
    const subtype = item.subtype ?? null
    if (seen.has(subtype)) return subtypes

    seen.add(subtype)
    subtypes.push(subtype)
    return subtypes
  }, [])
}

/** A job and its derived `subtypes`, so the two can never be written apart. */
function withQueueItems(job: OptJobWithQueueItems, queueItems: QueueItem[]): OptJobWithQueueItems {
  return { ...job, queue_items: queueItems, subtypes: deriveSubtypesFromQueueItems(queueItems) }
}

/**
 * Patches a single queue item, in place, across every cached
 * `useOptJobsWithQueueItems` result (all filter/pagination variants) that
 * happens to contain it. Matches only the root query key so it hits every
 * cached page/filter combination and leaves the rest untouched.
 *
 * A job that isn't loaded in a given cache entry is left as-is (no insert, no
 * invalidate). A job that IS loaded but doesn't yet carry this queue item
 * (e.g. its `queue_items` was empty, or a new subtype got queued after the
 * job was fetched) has the item appended, matched by `queueItem.opt_job`.
 * Either way the job's `subtypes` is re-derived from the resulting items, since
 * that field is a projection of them rather than a column of its own.
 */
export function patchOptJobWithQueueItemsCache(queueItem: QueueItem): void {
  queryClient.setQueriesData<OptJobWithQueueItems[]>({ queryKey: [OPT_JOB_WITH_QUEUE_ITEMS_QUERY_KEY] }, (old) => {
    if (!old) return old

    let didChange = false
    const nextJobs = old.map((job) => {
      if (job.id !== queueItem.opt_job) return job

      const currentQueueItem = job.queue_items.find((item) => item.id === queueItem.id)

      if (!currentQueueItem) {
        didChange = true
        return withQueueItems(job, [...job.queue_items, queueItem])
      }

      // Guard against out-of-order Pusher delivery.
      if (new Datetime(queueItem.updated_at).date <= new Datetime(currentQueueItem.updated_at).date) return job

      didChange = true
      return withQueueItems(
        job,
        job.queue_items.map((item) => (item.id === queueItem.id ? { ...item, ...queueItem } : item))
      )
    })

    return didChange ? nextJobs : old
  })
}
